import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Warning, UploadSimple } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NC, fmtEur, STATUS_LABEL } from './chequesApi';
import { DateField } from '@/components/ui/date-field';

const API = process.env.REACT_APP_BACKEND_URL;
const COLUMNS = ['patient_name', 'nutri_name', 'cheque_number', 'registration_date', 'consultation_date', 'gross', 'withholding_pct', 'filipa_pct', 'status'];

async function resolvePatientId(name) {
  if (!name) return '';
  try { const { data } = await axios.get(`${API}/api/patients?search=${encodeURIComponent(name.trim())}&limit=1`); return data[0]?.user_id || ''; } catch { return ''; }
}

export default function ChequeImportDialog({ open, onClose, staff, onImported }) {
  const [pasteText, setPasteText] = useState('');
  const [rows, setRows] = useState([]);
  const [preview, setPreview] = useState(null);
  const [accepted, setAccepted] = useState({});
  const [busy, setBusy] = useState(false);
  const [manualRow, setManualRow] = useState({ patient_name: '', nutri_id: '', cheque_number: '', registration_date: '', gross: '40', withholding_pct: '25', filipa_pct: '60' });

  const parsePaste = () => {
    const lines = pasteText.trim().split('\n').filter(Boolean);
    if (lines.length < 2) { toast.error('Cola pelo menos um cabeçalho e uma linha'); return; }
    const header = lines[0].split(',').map(h => h.trim());
    const parsed = lines.slice(1).map(line => {
      const cells = line.split(',').map(c => c.trim());
      const row = {};
      header.forEach((h, i) => { row[h] = cells[i] || ''; });
      return row;
    });
    setRows(r => [...r, ...parsed]);
    setPasteText('');
    toast.success(`${parsed.length} linha(s) adicionada(s)`);
  };

  const addManualRow = () => {
    if (!manualRow.patient_name || !manualRow.nutri_id) { toast.error('Paciente e nutricionista são obrigatórios'); return; }
    const nutri = staff.find(s => s.user_id === manualRow.nutri_id);
    setRows(r => [...r, { ...manualRow, nutri_name: nutri?.name || '' }]);
    setManualRow({ patient_name: '', nutri_id: '', cheque_number: '', registration_date: '', gross: '40', withholding_pct: '25', filipa_pct: '60' });
  };

  const runPreview = async () => {
    if (rows.length === 0) { toast.error('Sem linhas para importar'); return; }
    setBusy(true);
    try {
      const resolved = await Promise.all(rows.map(async (r) => {
        const patient_id = r.patient_id || await resolvePatientId(r.patient_name);
        const nutri_id = r.nutri_id || staff.find(s => (s.name || '').toLowerCase() === (r.nutri_name || '').toLowerCase())?.user_id || '';
        return { ...r, patient_id, nutri_id };
      }));
      setRows(resolved);
      const { data } = await axios.post(`${NC}/import/preview`, { rows: resolved });
      setPreview(data.rows);
      const acc = {}; data.rows.forEach(r => { acc[r.row_index] = !r.potential_duplicate; });
      setAccepted(acc);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro na pré-visualização'); }
    setBusy(false);
  };

  const confirmImport = async () => {
    const toSend = rows.filter((_, i) => accepted[i]);
    if (toSend.length === 0) { toast.error('Nenhuma linha selecionada'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${NC}/import/confirm`, { rows: toSend });
      toast.success(`${data.created} cheque(s) importado(s)`);
      setRows([]); setPreview(null); onImported(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao importar'); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cheque-import-dialog">
        <DialogHeader><DialogTitle>Importação inicial de cheques</DialogTitle></DialogHeader>

        {!preview ? (
          <div className="space-y-4">
            <div>
              <p className="text-xs text-brand-muted mb-1">Colar CSV (cabeçalho: {COLUMNS.join(', ')})</p>
              <Textarea value={pasteText} onChange={e => setPasteText(e.target.value)} rows={4} placeholder="patient_name,nutri_name,cheque_number,registration_date,consultation_date,gross,withholding_pct,filipa_pct,status" className="rounded-xl border-brand-border text-xs font-mono" data-testid="cheque-import-paste" />
              <button onClick={parsePaste} className="mt-2 text-xs text-brand-green flex items-center gap-1" data-testid="cheque-import-parse-btn"><UploadSimple size={13} /> Adicionar linhas coladas</button>
            </div>

            <div className="bg-brand-bg/40 rounded-xl p-3">
              <p className="text-xs font-medium text-brand-text mb-2">Ou adicionar cheque a cheque</p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input value={manualRow.patient_name} onChange={e => setManualRow(r => ({ ...r, patient_name: e.target.value }))} placeholder="Nome do paciente" className="rounded-lg border border-brand-border px-2 py-1.5 text-xs" data-testid="cheque-import-manual-patient" />
                <Select value={manualRow.nutri_id} onValueChange={v => setManualRow(r => ({ ...r, nutri_id: v }))}>
                  <SelectTrigger className="rounded-lg border-brand-border h-8 text-xs" data-testid="cheque-import-manual-nutri"><SelectValue placeholder="Nutricionista" /></SelectTrigger>
                  <SelectContent>{staff.map(s => <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
                <input value={manualRow.cheque_number} onChange={e => setManualRow(r => ({ ...r, cheque_number: e.target.value }))} placeholder="Nº cheque (opcional)" className="rounded-lg border border-brand-border px-2 py-1.5 text-xs" data-testid="cheque-import-manual-number" />
                <DateField value={manualRow.registration_date} onChange={e => setManualRow(r => ({ ...r, registration_date: e.target.value }))} className="rounded-lg border border-brand-border px-2 py-1.5 text-xs" data-testid="cheque-import-manual-date" />
              </div>
              <button onClick={addManualRow} className="text-xs text-brand-green flex items-center gap-1" data-testid="cheque-import-add-manual-btn"><Plus size={13} /> Adicionar à lista</button>
            </div>

            {rows.length > 0 && <p className="text-xs text-brand-muted" data-testid="cheque-import-rows-count">{rows.length} linha(s) prontas para pré-visualizar</p>}
            <button onClick={runPreview} disabled={busy || rows.length === 0} className="w-full bg-brand-text text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-40" data-testid="cheque-import-preview-btn">Pré-visualizar</button>
          </div>
        ) : (
          <div className="space-y-2">
            {preview.map(r => (
              <div key={r.row_index} className={`border rounded-xl p-3 flex items-center gap-3 ${r.potential_duplicate ? 'border-amber-300 bg-amber-50' : 'border-brand-border bg-white'}`} data-testid={`cheque-import-preview-row-${r.row_index}`}>
                <input type="checkbox" checked={!!accepted[r.row_index]} onChange={e => setAccepted(a => ({ ...a, [r.row_index]: e.target.checked }))} data-testid={`cheque-import-accept-${r.row_index}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-brand-text truncate">{r.patient_name} · {r.nutri_name} · {r.cheque_number || 'sem número'}</p>
                  <p className="text-[11px] text-brand-muted">Bruto {fmtEur(r.gross)} · Líquido {fmtEur(r.net_expected)} · {STATUS_LABEL[r.status] || r.status}</p>
                  {r.potential_duplicate && <p className="text-[11px] text-amber-700 flex items-center gap-1 mt-0.5"><Warning size={11} /> {r.duplicate_reason}, confirma para importar mesmo assim</p>}
                </div>
              </div>
            ))}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setPreview(null)} className="flex-1 py-2.5 rounded-full text-sm bg-brand-bg text-brand-muted" data-testid="cheque-import-back-btn">Voltar</button>
              <button onClick={confirmImport} disabled={busy} className="flex-1 py-2.5 rounded-full text-sm bg-brand-green text-white font-medium" data-testid="cheque-import-confirm-btn">Confirmar importação</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
